/**
 * Bundle a chat turn (question + narrative + sql + result rows + actions)
 * into a downloadable JSON or Markdown artifact. Pairs with the existing
 * CSV download in MessageCard so an analyst can hand off either the raw
 * data, a structured payload, or a human-readable digest.
 */

import { downloadCsv, toCsv } from "./csv";

export type ExportableTurn = {
  question: string;
  standaloneQuestion?: string | null;
  sql?: string | null;
  /**
   * Mirrors {@code ChatTurn.rows} — uses {@code columnNames} (not
   * {@code columns}) to match what the gateway streams back.
   */
  rows?: { columnNames: string[]; rows: unknown[][]; rowCount: number } | null;
  narrative?: {
    summary?: string;
    keyFindings?: string[];
    followUpQuestions?: string[];
    proposedActions?: Array<{ actionType: string; summary: string; rationale?: string }>;
  } | null;
  conversationId?: string | null;
  ranAt?: string | null;
};

function downloadFile(filename: string, data: string, mime: string) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadJson(filename: string, turn: ExportableTurn): void {
  const payload = {
    exportedAt: new Date().toISOString(),
    conversationId: turn.conversationId ?? null,
    question: turn.question,
    standaloneQuestion: turn.standaloneQuestion ?? null,
    sql: turn.sql ?? null,
    narrative: turn.narrative ?? null,
    result: turn.rows ?? null,
  };
  downloadFile(filename, JSON.stringify(payload, null, 2), "application/json");
}

export function downloadMarkdown(filename: string, turn: ExportableTurn): void {
  const md: string[] = [];
  md.push(`# ${turn.question}`);
  md.push("");
  if (turn.ranAt) md.push(`*Generated ${turn.ranAt}*`);
  md.push("");

  if (turn.narrative?.summary) {
    md.push("## Summary");
    md.push(turn.narrative.summary);
    md.push("");
  }

  if (turn.narrative?.keyFindings && turn.narrative.keyFindings.length > 0) {
    md.push("## Key findings");
    for (const f of turn.narrative.keyFindings) md.push(`- ${f}`);
    md.push("");
  }

  if (turn.sql) {
    md.push("## SQL");
    md.push("```sql");
    md.push(turn.sql);
    md.push("```");
    md.push("");
  }

  if (turn.rows && turn.rows.columnNames.length > 0 && turn.rows.rows.length > 0) {
    md.push(`## Results (${turn.rows.rowCount} rows)`);
    md.push("");
    md.push("| " + turn.rows.columnNames.join(" | ") + " |");
    md.push("| " + turn.rows.columnNames.map(() => "---").join(" | ") + " |");
    // Cap the table at 200 rows in the markdown — past that, point at the CSV.
    const cap = 200;
    const slice = turn.rows.rows.slice(0, cap);
    for (const row of slice) {
      md.push("| " + row.map((v) => formatCell(v)).join(" | ") + " |");
    }
    if (turn.rows.rows.length > cap) {
      md.push("");
      md.push(`*…and ${turn.rows.rows.length - cap} more rows. Use the CSV export for the full table.*`);
    }
    md.push("");
  }

  if (turn.narrative?.followUpQuestions && turn.narrative.followUpQuestions.length > 0) {
    md.push("## Follow-up questions");
    for (const q of turn.narrative.followUpQuestions) md.push(`- ${q}`);
    md.push("");
  }

  if (turn.narrative?.proposedActions && turn.narrative.proposedActions.length > 0) {
    md.push("## Proposed actions");
    for (const a of turn.narrative.proposedActions) {
      md.push(`- **${a.actionType}** — ${a.summary}`);
      if (a.rationale) md.push(`  - ${a.rationale}`);
    }
    md.push("");
  }

  downloadFile(filename, md.join("\n"), "text/markdown;charset=utf-8");
}

export function downloadTurnCsv(filename: string, turn: ExportableTurn): void {
  if (!turn.rows) return;
  downloadCsv(filename, toCsv(turn.rows.columnNames, turn.rows.rows));
}

function formatCell(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  // Markdown table cells must escape `|` and newlines.
  return s.replace(/\|/g, "\\|").replace(/\r?\n/g, " ");
}
